import React, { forwardRef } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../../ui-components/table/table";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import XGSNavLink from "../../../ui-components/xgs-nav-link/xgsNavLink";
import { ReactComponent as ChartIcon } from "../../../images/chart.svg";
import Tag from "../../../ui-components/molecules/tag/tag";
import { TagColor } from "../../../app/data/common/tagColor";

import "./accountsList.scss";

export interface TeamAccountsProps {
  ref?: any;
  data: [];
  onRowClicked?: (row: any) => void;
  onDeleteItem?: (row: any) => void;
  onInfiniteScroll: () => void;
  isLoading: boolean;
  infiniteScrollLoading: boolean;
  infiniteScrollHasNext: boolean;
  type?: string;
  rowHeight?: number;
  hideActions?: boolean;
}

const columnHelper = createColumnHelper<any>();

const AccountsList: React.FC<TeamAccountsProps> = forwardRef((props, ref) => {
  const columns = [
    columnHelper.accessor("accountNumber", {
      id: "accountNumber",
      header: "Account #",
      cell: ({ row }) => (
        <span className={row.original.deleted ? "xgs-teams__deleted-account" : ""}>
          {row.original.accountNumber}
        </span>
      ),
      size: 80,
      minSize: 80,
    }),
    columnHelper.accessor("name", {
      id: "name",
      header: "Customer name",
      cell: ({ row }) => (
        <span className={row.original.deleted ? "xgs-teams__deleted-account" : ""}>
          {row.original.paypoint && (
            <XGSIcon
              icon={XGSIcons.faDollarSign}
              className="xgs-teams__details__payor-account"
              title="Paypoint account"
            />
          )}
          {row.original.name}
        </span>
      ),
      size: 128,
      minSize: 128,
    }),
    columnHelper.display({
      id: "addressColumn",
      header: "Address",
      cell: ({ row }) => (
        <span>
          {row.original.address?.line1 ? `${row.original.address.line1}, ` : ""}{row.original.address?.line2 ? `${row.original.address.line2}, ` : ""}{row.original.address?.city ? `${row.original.address.city}, ` : ""}{row.original.address?.state ? `${row.original.address.state}, ` : ""}{row.original.address?.zip}
        </span>
      ),
      size: 203,
      minSize: 200,
    }),
    ...props.type === "AGENT" ? [] : [
      columnHelper.accessor("isAvailabilityHoursFilled", {
        id: "availability",        
        header: "Availability time",        
        cell: (cellProps) => (
          cellProps.getValue() === "AVAILABLE" ? (
            <Tag mods={{ color: TagColor.GREEN }}>
              AVAILABLE
            </Tag>
          ) : cellProps.getValue() === "NA" ? (
            <Tag mods={{ color: TagColor.GREY }}>
              N/A
            </Tag>
          ) : cellProps.getValue() === "MISSING" ? (
            <Tag mods={{ color: TagColor.GREY }}>
              MISSING
            </Tag>
          ) : (
            <Tag mods={{ color: TagColor.GREY }}>
              PARTIAL
            </Tag>
          )
        ),
        size: 120,
        minSize: 120,
      }),
      columnHelper.accessor("isContactsFilled", {
        id: "contacts",        
        header: "Contacts",        
        cell: (cellProps) => (
          cellProps.getValue() === "AVAILABLE" ? (
            <Tag mods={{ color: TagColor.GREEN }}>
              AVAILABLE
            </Tag>
          ) : cellProps.getValue() === "NA" ? (
            <Tag mods={{ color: TagColor.GREY }}>
              N/A
            </Tag>
          ) : cellProps.getValue() === "MISSING" ? (
            <Tag mods={{ color: TagColor.GREY }}>
              MISSING
            </Tag>
          ) : (
            <Tag mods={{ color: TagColor.GREY }}>
              PARTIAL
            </Tag>
          )
        ),
        size: 100,
        minSize: 100,
      }),
    ],
    ...props.hideActions ? [] : [columnHelper.display({
      id: "actionColumn",      
      header: "",
      cell: ({ row }) => (
        <div className="text-center">
          {!row.original.deleted && (
            <span className="xgs-company__action-buttons">
              <XGSNavLink
                className="xgs-company__small-button xgs-company__small-button--color_blue"
                to={`/customers/accounts/${row.original.accountNumber}`}
                onClick={(e) => e.stopPropagation()}
              ><ChartIcon /></XGSNavLink>
              {props.onDeleteItem && <Button
                className="xgs-company__small-button"
                type="button"
                theme={ButtonThemes.gray}
                spinner={false}
                onClick={(e) => {
                  e.stopPropagation();
                  props.onDeleteItem?.(row);
                }}
              >
                <XGSIcon
                  icon={XGSIcons.faTimes}
                  size="sm"
                />
              </Button>}
            </span>
          )}
        </div>
      ),
      size: 102,
      minSize: 102,
    })]
  ];

  return (
    <Table
      ref={ref}
      autoHeight={true}
      isLoading={props.isLoading}
      columns={columns}
      data={props.data}
      onRowClicked={props.onRowClicked}
      cursorPointer={!!props.onRowClicked}
      rowHeight={props.rowHeight || 76}
      minTableHeight={400}
      noResultsText="There are no accounts"
      infiniteScroll={true}
      infiniteScrollLoading={props.infiniteScrollLoading}
      infiniteScrollHasNext={props.infiniteScrollHasNext}
      onInfiniteScroll={props.onInfiniteScroll}
      responsive={true}
    />
  );
});

export default AccountsList;
